import React from "react";
import { BarRow, DataTable, PALETTE, formatCurrency, formatPercent } from "./Primitives";

const PHASE_LABELS = {
    PRE_ARRIVAL: "Pre-arrival",
    IN_STAY: "In-stay",
    PRE_DEPARTURE: "Pre-departure",
    BOTH: "Any stage",
};

const PHASE_STYLES = {
    PRE_ARRIVAL: { color: PALETTE.blue, background: "#EEF4FF" },
    IN_STAY: { color: PALETTE.purple, background: "#f5f3ff" },
    PRE_DEPARTURE: { color: PALETTE.amber, background: "#FFFBEB" },
    BOTH: { color: PALETTE.green, background: "#F0FDF4" },
};

const TRIGGER_LABELS = {
    PRE_ARRIVAL_T3D: "3 days before arrival",
    PRE_ARRIVAL_T2D: "2 days before arrival",
    PRE_ARRIVAL_T1D: "1 day before arrival",
    ARRIVAL_DAY: "Arrival day",
    CHECKIN_PLUS_1H: "1h after check-in",
    FIRST_EVENING: "First evening",
    NEXT_MORNING: "Next morning",
    MID_STAY: "Mid-stay",
    DAY_BEFORE_CHECKOUT: "Day before checkout",
    CHECKOUT_MORNING: "Checkout morning",
};

const offerTerms = (row) => {
    if (row.offer_type === "PERCENT_OFF") {
        return `${row.offer_value}% off`;
    }
    if (row.offer_type === "AMOUNT_OFF") {
        return row.min_order_amount > 0
            ? `${formatCurrency(row.offer_value)} off above ${formatCurrency(row.min_order_amount)}`
            : `${formatCurrency(row.offer_value)} off`;
    }
    return "—";
};

const conversionColor = (rate) => {
    if (rate >= 55) return PALETTE.green;
    if (rate >= 30) return PALETTE.amber;
    return PALETTE.red;
};

export default function UpsellTab({ upsell }) {
    const totals = upsell?.totals ?? {};
    const offers = upsell?.offers ?? [];
    const byPhase = upsell?.by_phase ?? [];

    const stats = [
        { label: "Offers Sent", value: totals.sent ?? 0, color: PALETTE.blue },
        { label: "Offers Applied", value: totals.applied ?? 0, color: PALETTE.green },
        { label: "Conversion Rate", value: formatPercent(totals.conversion_rate), color: PALETTE.purple },
        { label: "Upsell Revenue", value: formatCurrency(totals.revenue), color: PALETTE.red },
    ];

    const columns = [
        {
            key: "title",
            label: "Offer Name",
            width: "2fr",
            render: (row) => (
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1a1a2e" }}>{row.title}</div>
                    {row.service && <div style={{ fontSize: 11, color: "#9097ac" }}>{row.service}</div>}
                </div>
            ),
        },
        {
            key: "offer",
            label: "Offer",
            width: "1.3fr",
            render: (row) => (
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "#1a1a2e" }}>
                    {offerTerms(row)}
                </span>
            ),
        },
        {
            key: "stage",
            label: "Sent At",
            width: "1.2fr",
            render: (row) => {
                const style = PHASE_STYLES[row.send_phase] || { color: "#69708A", background: "#F0F2F7" };
                return (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "2px 8px",
                                borderRadius: 20,
                                ...style,
                            }}
                        >
                            {PHASE_LABELS[row.send_phase] || "Unscheduled"}
                        </span>
                        {TRIGGER_LABELS[row.lifecycle_trigger] && (
                            <span style={{ fontSize: 10, color: "#9097ac" }}>
                                {TRIGGER_LABELS[row.lifecycle_trigger]}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            key: "sent",
            label: "Sent",
            render: (row) => <span style={{ fontSize: 13.5, color: "#69708A" }}>{row.sent}</span>,
        },
        {
            key: "applied",
            label: "Applied",
            render: (row) => (
                <span style={{ fontSize: 13.5, fontWeight: 700, color: PALETTE.green }}>{row.applied}</span>
            ),
        },
        {
            key: "conversion_rate",
            label: "Conversion",
            render: (row) =>
                row.sent > 0 ? (
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: conversionColor(row.conversion_rate) }}>
                        {formatPercent(row.conversion_rate)}
                    </span>
                ) : (
                    <span style={{ fontSize: 12, color: "#9097ac" }} title="No sends recorded in this period">
                        —
                    </span>
                ),
        },
        {
            key: "revenue_per_send",
            label: "Rev / Send",
            render: (row) => (
                <span style={{ fontSize: 13, color: row.sent > 0 ? "#69708A" : "#9097ac" }}>
                    {row.sent > 0 ? formatCurrency(row.revenue_per_send) : "—"}
                </span>
            ),
        },
        {
            key: "discount_given",
            label: "Discount",
            render: (row) => (
                <span style={{ fontSize: 13, color: "#9097ac" }}>{formatCurrency(row.discount_given)}</span>
            ),
        },
        {
            key: "revenue",
            label: "Revenue",
            align: "right",
            render: (row) => (
                <span style={{ fontSize: 13.5, fontWeight: 700, color: PALETTE.red }}>
                    {formatCurrency(row.revenue)}
                </span>
            ),
        },
    ];

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
                {stats.map((stat) => (
                    <div
                        key={stat.label}
                        style={{
                            background: "#fff",
                            borderRadius: 12,
                            padding: "14px 16px",
                            border: "1.5px solid #ECEEF5",
                        }}
                    >
                        <div
                            style={{
                                fontSize: 10,
                                color: "#9097ac",
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                marginBottom: 5,
                            }}
                        >
                            {stat.label.toUpperCase()}
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: stat.color }}>{stat.value}</div>
                    </div>
                ))}
            </div>

            <DataTable
                columns={columns}
                rows={offers.map((row) => ({ ...row, key: row.promotion_id }))}
                emptyMessage="No promotions were sent in this period."
            />

            {byPhase.length > 0 && (
                <div
                    style={{
                        background: "#fff",
                        borderRadius: 14,
                        border: "1.5px solid #ECEEF5",
                        padding: "18px 20px",
                    }}
                >
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: "#1a1a2e", marginBottom: 14 }}>
                        Conversion by Journey Stage
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                        {byPhase.map((row) => (
                            <BarRow
                                key={row.phase}
                                label={PHASE_LABELS[row.phase] || "Unscheduled"}
                                value={row.conversion_rate}
                                max={100}
                                color={PALETTE.purple}
                                labelWidth={106}
                                displayWidth={96}
                                display={
                                    <span style={{ fontWeight: 400, color: "#9097ac" }}>
                                        {row.applied}/{row.sent} applied
                                    </span>
                                }
                                details={[
                                    {
                                        color: PALETTE.purple,
                                        label: "conversion",
                                        value: row.sent > 0 ? formatPercent(row.conversion_rate) : "—",
                                    },
                                    { label: "offers sent", value: row.sent },
                                    { label: "offers applied", value: row.applied },
                                    { label: "revenue", value: formatCurrency(row.revenue) },
                                ]}
                                trailing={
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: "#1a1a2e",
                                            width: 44,
                                            textAlign: "right",
                                        }}
                                    >
                                        {row.sent > 0 ? formatPercent(row.conversion_rate) : "—"}
                                    </span>
                                }
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
